import { useCallback, useEffect, useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";

export default function InternalReminderDialog({
  open,
  onOpenChange,
  context,
  onSent,
}) {
  const [message, setMessage] = useState("");
  const [comment, setComment] = useState("");
  const [recipientUserId, setRecipientUserId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !context) return;
    setMessage(context.defaultMessage || "");
    setComment("");
    setRecipientUserId(context.recipientUserId || "");
  }, [open, context]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Escriba el mensaje del recordatorio.");
      return;
    }
    setBusy(true);
    try {
      await despachoFiscalInfoApi.sendInternalReminder({
        fiscal_client_id: context?.fiscalClientId,
        compliance_instance_id: context?.complianceInstanceId,
        despacho_task_id: context?.despachoTaskId,
        recipient_user_id: recipientUserId || undefined,
        message: message.trim(),
        comment: comment.trim() || undefined,
        post_to_chat: true,
      });
      toast.success("Recordatorio enviado al chat interno.");
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4" aria-hidden />
            Enviar recordatorio interno
          </DialogTitle>
          <DialogDescription>
            Se publicará en el chat interno del equipo. No se envía WhatsApp ni correo al cliente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {context?.recipientLabel ? (
            <p className="text-slate-600 m-0">
              Destinatario sugerido: <strong>{context.recipientLabel}</strong>
            </p>
          ) : null}
          <SpellcheckTextarea
            id="reminder-message"
            label="Mensaje"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
          <SpellcheckTextarea
            id="reminder-comment"
            label="Comentario adicional (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSend} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden /> : null}
            Enviar al chat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useEntityAlerts(entityType, entityId) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) {
      setAlerts([]);
      return;
    }
    setLoading(true);
    try {
      const params =
        entityType === "task"
          ? { despacho_task_id: entityId }
          : { compliance_instance_id: entityId };
      const rows = await despachoFiscalInfoApi.listFiscalAlerts(params);
      setAlerts(rows);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const lastSent = alerts
    .filter((a) => a.sent_at)
    .sort((a, b) => String(b.sent_at).localeCompare(String(a.sent_at)))[0];

  return { alerts, loading, lastSent, reload: load };
}
